/**
 * PDF Utilities
 * 
 * Helper functions for handling PDF generation and display in the frontend
 */

import { toast } from '@etherisc/ui-kit';
import { generateFarmerPdf, generateProductMethodologyPdf, openPdfInNewWindow } from '../services/documentGeneration.service';

/**
 * Handle farmer PDF generation with proper loading states and error handling
 * 
 * @param farmerId - ID of the farmer
 * @param farmerName - Name of the farmer (for user feedback)
 * @returns Promise that resolves when PDF is opened/downloaded
 */
export async function handleGenerateFarmerPdf(
  farmerId: string,
  farmerName: string
): Promise<void> {
  const loadingToast = toast.loading(`Generating PDF for ${farmerName}...`);

  try {
    // Generate the PDF
    const result = await generateFarmerPdf(farmerId);

    if (result.success && result.pdfUrl) {
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Show success message
      toast.success(`PDF generated for ${farmerName}`);
      
      // Open PDF in new window
      await openPdfInNewWindow(result.pdfUrl, result.filename);
    } else {
      throw new Error(result.error || 'Failed to generate PDF');
    }
  } catch (error) {
    console.error('❌ PDF generation error:', error);
    
    // Dismiss loading toast
    toast.dismiss(loadingToast);
    
    // Show error message
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Failed to generate PDF';
      
    toast.error(`PDF generation failed: ${errorMessage}`);
    
    // Re-throw for component to handle if needed
    throw error;
  }
}

/**
 * Handle product methodology PDF generation with proper loading states and error handling
 * 
 * @param productId - ID of the product
 * @param productName - Name of the product (for user feedback)
 * @returns Promise that resolves when PDF is opened/downloaded
 */
export async function handleGenerateProductMethodologyPdf(
  productId: string,
  productName: string
): Promise<void> {
  const loadingToast = toast.loading(`Generating methodology PDF for ${productName}...`);

  try {
    // Generate the PDF
    const result = await generateProductMethodologyPdf(productId);

    if (result.success && result.pdfUrl) {
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Show success message
      toast.success(`Methodology PDF generated for ${productName}`);
      
      // Open PDF in new window
      await openPdfInNewWindow(result.pdfUrl, result.filename);
    } else {
      throw new Error(result.error || 'Failed to generate methodology PDF');
    }
  } catch (error) {
    console.error('❌ Methodology PDF generation error:', error);
    
    // Dismiss loading toast
    toast.dismiss(loadingToast);
    
    // Show error message
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Failed to generate methodology PDF';
      
    toast.error(`Methodology PDF generation failed: ${errorMessage}`);
    
    // Re-throw for component to handle if needed
    throw error;
  }
}

/**
 * Check if the browser supports PDF viewing
 * 
 * @returns True if browser can display PDFs
 */
export function canViewPdf(): boolean {
  // Check if browser has PDF plugin or native support
  if (navigator.mimeTypes && navigator.mimeTypes.namedItem('application/pdf')) {
    return true;
  }
  
  // Check for Chrome PDF viewer
  if (navigator.userAgent.includes('Chrome')) {
    return true;
  }
  
  // Default to true for modern browsers
  return true;
}

/**
 * Get appropriate PDF action text based on browser capabilities
 * 
 * @returns Text for PDF action button
 */
export function getPdfActionText(): string {
  return canViewPdf() ? 'View PDF' : 'Download PDF';
}